import { clamp, MatrixLike, Point, Region, transformPoint } from './geometry';

const IDENTITY: MatrixLike = {a: 1, b: 0, c: 0, d: 1, e: 0, f: 0};

function inverseOf(matrix: MatrixLike): MatrixLike
{
  const inverse = DOMMatrixReadOnly.fromMatrix(matrix).inverse();

  return isFinite(inverse.a) ? inverse : IDENTITY;
}

function between(value: number, low: number, high: number): number
{
  return clamp(value, low, Math.max(low, high));
}

export class CropArea
{
  public static readonly MAX_ZOOM: number = 5.00;

  private readonly matrix: MatrixLike;
  private readonly inverse: MatrixLike;

  constructor(private readonly extent: Region, matrix?: MatrixLike)
  {
    this.matrix = matrix === undefined ? IDENTITY : matrix;
    this.inverse = inverseOf(this.matrix);
  }

  public get corners(): Point[]
  {
    return this.extent.corners.map(
        (corner) => transformPoint(this.matrix, corner));
  }

  public get bounds(): Region
  {
    return this.extent.transformedBy(this.matrix);
  }

  public largestSelection(aspectRatio: number): Region
  {
    const height = Math.min(
        this.roomAlong(
            this.extent.width,
            Math.abs(this.inverse.a) * aspectRatio + Math.abs(this.inverse.c)),
        this.roomAlong(
            this.extent.height,
            Math.abs(this.inverse.b) * aspectRatio + Math.abs(this.inverse.d)));

    return new Region(0, 0, aspectRatio * height, height)
        .centeredAt(transformPoint(this.matrix, this.extent.center));
  }

  public smallestSelection(aspectRatio: number): Region
  {
    const largest = this.largestSelection(aspectRatio);

    return new Region(
        0,
        0,
        largest.width / CropArea.MAX_ZOOM,
        largest.height / CropArea.MAX_ZOOM).centeredAt(largest.center);
  }

  public coverScaleFor(frame: Region): number
  {
    const largest = this.largestSelection(frame.width / frame.height);

    return frame.width / largest.width;
  }

  public clampSelection(selection: Region): Region
  {
    if (selection.width <= 0 || selection.height <= 0) {
      return selection;
    }

    const largest = this.largestSelection(selection.width / selection.height);
    const fitting = Math.min(
        1,
        largest.width / selection.width,
        largest.height / selection.height);

    const width = selection.width * fitting;
    const height = selection.height * fitting;

    const box = this.sizeWithinExtent(width, height);
    const center = transformPoint(this.inverse, selection.center);

    const fitted = new Point(
        between(
            center.x,
            this.extent.x + box.x / 2,
            this.extent.bottomRight.x - box.x / 2),
        between(
            center.y,
            this.extent.y + box.y / 2,
            this.extent.bottomRight.y - box.y / 2));

    return new Region(0, 0, width, height)
        .centeredAt(transformPoint(this.matrix, fitted));
  }

  private sizeWithinExtent(width: number, height: number): Point
  {
    return new Point(
        Math.abs(this.inverse.a) * width + Math.abs(this.inverse.c) * height,
        Math.abs(this.inverse.b) * width + Math.abs(this.inverse.d) * height);
  }

  private roomAlong(available: number, needed: number): number
  {
    return needed === 0 ? Infinity : available / needed;
  }
}
